import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card } from '../../components/ui/Card';
import { Loader2, Download } from 'lucide-react';
import { exportReportPdf, PdfHeader, PdfKpis, PdfFooter } from './reportPdf';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth } from '../../context/AuthContext';
import { WUNDER_BRAND_ID } from '../../config/brandGroups';
import { scaleCampaignRows } from './CampaignReport';

// Wunder marka raporu iskeleti (adidas FW26, ASICS Eylül): Campaign Report'taki maliyet
// paneli + platform özeti (kampanya adı yok) + aylık kırılım + wunder.com.tr (ikas online) satışları.
// config: { title, campaignLike, ikasBrand, brandLabel, logo, presets, minDate, campaignNote }
// children(brandId, startDate, endDate) → markaya özel ek bölüm.

export const API = 'https://api.glowytics.com';

export const formatCurrency = (num) => `₺${Math.round(Number(num || 0)).toLocaleString('en-US')}`;
export const fmtEn = (n) => (!n ? '-' : Math.round(Number(n)).toLocaleString('en-US')); // MetricsTable ile aynı biçim
export const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const METRIC_KEYS = ['impressions', 'clicks', 'cost', 'conversions', 'revenue', 'ga4Sessions', 'ga4Purchases', 'ga4Revenue'];
export const MONTHS_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

// Günlük satırları kampanya adı campaignLike içerenlere süzüp ortak alan adlarına çevirir (month = YYYY-MM).
function normalize(rows, map, campaignLike) {
  const out = [];
  for (const row of rows || []) {
    const cn = (row.campaign_name || '').trim();
    if (!cn || !cn.toLowerCase().includes(campaignLike)) continue;
    const r = { campaignName: cn, month: String(row.date || '').slice(0, 7) };
    for (const [k, src] of Object.entries(map)) r[k] = Number(row[src] || 0);
    out.push(r);
  }
  return out;
}

function aggregate(rows, keyFn = (r) => r.campaignName) {
  const by = {};
  for (const r of rows) {
    const key = keyFn(r);
    if (!by[key]) by[key] = { campaignName: r.campaignName, month: r.month, ...Object.fromEntries(METRIC_KEYS.map((k) => [k, 0])) };
    for (const k of METRIC_KEYS) by[key][k] += r[k] || 0;
  }
  return Object.values(by)
    .filter((x) => x.cost > 0 || x.clicks > 0 || x.impressions > 0)
    .sort((a, b) => b.cost - a.cost);
}

export function useBrandFetch(url, brandId) {
  const [state, setState] = useState({ loading: false, data: null, error: null });
  useEffect(() => {
    if (!url) return undefined;
    let cancelled = false;
    setState({ loading: true, data: null, error: null });
    fetch(url, { credentials: 'include', headers: { 'X-Brand-ID': brandId } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Veri alınamadı'))))
      .then((j) => { if (!cancelled) setState({ loading: false, data: j, error: null }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, data: null, error: e.message }); });
    return () => { cancelled = true; };
  }, [url, brandId]);
  return state;
}

export const Spinner = () => (
  <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
);

// Platform özeti: kampanya adları gösterilmiyor, Google / Meta tek satır. Override
// Campaign Report ile aynı ölçekle uygulanıyor (scaleCampaignRows).
function PlatformSummary({ googleData, metaData, costOverrideGoogle, costOverrideMeta }) {
  const sum = (rows) => rows.reduce((t, r) => {
    for (const k of ['cost', 'conversions', 'revenue']) t[k] += r[k] || 0;
    return t;
  }, { cost: 0, conversions: 0, revenue: 0 });
  const rows = [
    { name: 'Google Ads', icon: '/google_ads-512.webp', ...sum(scaleCampaignRows(googleData, costOverrideGoogle, true)) },
    { name: 'Meta Ads', icon: '/6033716.png', ...sum(scaleCampaignRows(metaData, costOverrideMeta, false, null)) },
  ];
  const total = sum(rows);
  const th = 'py-3 px-2.5 text-right font-semibold text-gray-600 first:text-left whitespace-nowrap text-xs';
  const td = 'py-2.5 px-2.5 text-right text-gray-700';
  const cells = (m) => [
    m.cost > 0 ? formatCurrency(m.cost) : '-',
    m.conversions > 0 ? fmtEn(m.conversions) : '-',
    m.revenue > 0 ? formatCurrency(m.revenue) : '-',
    m.cost > 0 ? `${(m.revenue / m.cost).toFixed(2)}x` : '-',
  ];

  return (
    <div className="mb-8">
      <PdfKpis items={[
        { label: 'Reklam harcaması', value: formatCurrency(total.cost) },
        { label: 'Reklam cirosu', value: formatCurrency(total.revenue) },
        { label: 'Dönüşüm', value: fmtEn(total.conversions) },
        { label: 'ROAS', value: total.cost > 0 ? `${(total.revenue / total.cost).toFixed(2)}x` : '-', accent: true },
      ]} />
      <h2 className="text-lg font-semibold text-gray-800 mb-3">Reklam performansı</h2>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50/80 border-b border-gray-200">
              {['Platform', 'Cost', 'Conv.', 'Revenue', 'ROAS'].map((h) => <th key={h} className={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((r) => (
              <tr key={r.name} className="bg-white">
                <td className="py-2.5 px-2.5 text-left font-medium text-gray-800">
                  <span className="inline-flex items-center gap-2"><img src={r.icon} alt={r.name} className="w-4 h-4" />{r.name}</span>
                </td>
                {cells(r).map((v, i) => <td key={i} className={td}>{v}</td>)}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-100 font-bold text-gray-800 border-t border-gray-200">
              <td className="py-3 px-2.5 text-left">Total</td>
              {cells(total).map((v, i) => <td key={i} className="py-3 px-2.5 text-right">{v}</td>)}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// Aylık kırılım: override varsa her metrik, kampanya tablosunun toplamındaki oranla
// (gösterilen toplam / gerçek toplam) her aya aynen uygulanır → aylar tablo toplamına eşit.
function MonthlyTable({ title, icon, rows, campaignData, costOverride, isGoogleAds }) {
  const months = useMemo(() => {
    const real = aggregate(rows, (r) => r.month).sort((a, b) => a.month.localeCompare(b.month));
    const shown = scaleCampaignRows(campaignData, costOverride, isGoogleAds, null);
    const ratio = {};
    for (const k of ['impressions', 'clicks', 'cost', 'conversions', 'revenue']) {
      const r = campaignData.reduce((s, x) => s + (x[k] || 0), 0);
      const d = shown.reduce((s, x) => s + (x[k] || 0), 0);
      ratio[k] = r > 0 ? d / r : 1;
    }
    return real.map((m) => ({
      month: m.month,
      impressions: m.impressions * ratio.impressions,
      clicks: m.clicks * ratio.clicks,
      cost: m.cost * ratio.cost,
      conversions: m.conversions * ratio.conversions,
      revenue: m.revenue * ratio.revenue,
    }));
  }, [rows, campaignData, costOverride, isGoogleAds]);

  if (!months.length) return null;
  const total = months.reduce((t, m) => {
    for (const k of ['impressions', 'clicks', 'cost', 'conversions', 'revenue']) t[k] += m[k];
    return t;
  }, { impressions: 0, clicks: 0, cost: 0, conversions: 0, revenue: 0 });
  const th = 'py-3 px-2.5 text-right font-semibold text-gray-600 first:text-left whitespace-nowrap text-xs';
  const td = 'py-2.5 px-2.5 text-right text-gray-700';
  const cells = (m) => [
    m.cost > 0 ? formatCurrency(m.cost) : '-',
    m.conversions > 0 ? fmtEn(m.conversions) : '-',
    m.revenue > 0 ? formatCurrency(m.revenue) : '-',
    m.cost > 0 ? `${(m.revenue / m.cost).toFixed(2)}x` : '-',
  ];

  return (
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
        <img src={icon} alt={title} className="w-4 h-4" />
        {title}
      </h3>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50/80 border-b border-gray-200">
              {['Ay', 'Cost', 'Conv.', 'Revenue', 'ROAS'].map((h) => <th key={h} className={th}>{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {months.map((m) => (
              <tr key={m.month} className="bg-white hover:bg-gray-50/50">
                <td className="py-2.5 px-2.5 text-left font-medium text-gray-800">{MONTHS_TR[Number(m.month.slice(5, 7)) - 1]} {m.month.slice(0, 4)}</td>
                {cells(m).map((v, i) => <td key={i} className={td}>{v}</td>)}
              </tr>
            ))}
          </tbody>
          {months.length > 1 && (
            <tfoot>
              <tr className="bg-gray-100 font-bold text-gray-800 border-t border-gray-200">
                <td className="py-3 px-2.5 text-left">Total</td>
                {cells(total).map((v, i) => <td key={i} className="py-3 px-2.5 text-right">{v}</td>)}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function MonthlySection({ googleRows, metaRows, googleData, metaData, costOverrideGoogle, costOverrideMeta }) {
  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold text-gray-800 mb-3">Aylık kırılım</h2>
      <MonthlyTable title="Google Ads" icon="/google_ads-512.webp" rows={googleRows} campaignData={googleData} costOverride={costOverrideGoogle} isGoogleAds />
      <MonthlyTable title="Meta Ads" icon="/6033716.png" rows={metaRows} campaignData={metaData} costOverride={costOverrideMeta} isGoogleAds={false} />
    </div>
  );
}

// wunder.com.tr satışları (ikas, yalnız online kanal); mağaza ve panel siparişleri hariç.
function IkasSalesSection({ brandId, startDate, endDate, ikasBrand, brandLabel }) {
  const url = `${API}/api/brand-campaign-report/ikas-products?brandName=${encodeURIComponent(ikasBrand)}&salesChannel=wunder&startDate=${ymd(startDate)}&endDate=${ymd(endDate)}`;
  const { loading, data, error } = useBrandFetch(url, brandId);
  const totals = data?.totals;
  const months = useMemo(() => {
    const by = {};
    for (const d of data?.daily || []) {
      const m = String(d.day).slice(0, 7);
      if (!by[m]) by[m] = { month: m, orders: 0, items: 0, revenue: 0 };
      by[m].orders += d.orders; by[m].items += d.items; by[m].revenue += d.revenue;
    }
    return Object.values(by).sort((a, b) => a.month.localeCompare(b.month));
  }, [data]);
  const th = 'py-3 px-2.5 text-right font-semibold text-gray-600 first:text-left whitespace-nowrap text-xs';
  const td = 'py-2.5 px-2.5 text-right text-gray-700';
  const SalesTable = ({ head, rows, label }) => (
    <div className="overflow-x-auto rounded-xl border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50/80 border-b border-gray-200">
            {[head, 'Sipariş', 'Adet', 'Ciro', 'Pay'].map((h) => <th key={h} className={th}>{h}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => (
            <tr key={label(r)} className="bg-white">
              <td className="py-2.5 px-2.5 text-left font-medium text-gray-800">{label(r)}</td>
              <td className={td}>{fmtEn(r.orders)}</td>
              <td className={td}>{fmtEn(r.items)}</td>
              <td className={td}>{formatCurrency(r.revenue)}</td>
              <td className={td}>{totals?.revenue ? `%${((r.revenue / totals.revenue) * 100).toFixed(1)}` : '-'}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="bg-gray-100 font-bold text-gray-800 border-t border-gray-200">
            <td className="py-3 px-2.5 text-left">Total</td>
            <td className="py-3 px-2.5 text-right">{fmtEn(totals?.orders)}</td>
            <td className="py-3 px-2.5 text-right">{fmtEn(totals?.items)}</td>
            <td className="py-3 px-2.5 text-right">{formatCurrency(totals?.revenue)}</td>
            <td className="py-3 px-2.5 text-right">%100</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );

  return (
    <div className="mt-10">
      <h2 className="text-lg font-semibold text-gray-800 mb-1">wunder.com.tr · {brandLabel} Satışları</h2>
      <p className="text-sm text-gray-500 mb-4">Seçili tarih aralığında wunder.com.tr'den gelen {brandLabel} siparişleri (online)</p>
      {loading ? <Spinner /> : error ? (
        <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>
      ) : !totals?.orders ? (
        <div className="p-3 rounded-lg bg-gray-50 text-gray-600 text-sm">Seçili aralıkta {brandLabel} satışı yok.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {[
              { label: 'Ciro', value: formatCurrency(totals.revenue) },
              { label: 'Sipariş', value: fmtEn(totals.orders) },
              { label: 'Satış adedi', value: fmtEn(totals.items) },
              { label: 'Satılan ürün (model)', value: fmtEn(totals.products) },
            ].map((k) => (
              <div key={k.label} className="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                <div className="text-xs font-medium text-gray-500">{k.label}</div>
                <div className="mt-1 text-xl font-semibold text-gray-800">{k.value}</div>
              </div>
            ))}
          </div>
          <div className="mb-6">
            <SalesTable head="Ay" rows={months} label={(r) => `${MONTHS_TR[Number(r.month.slice(5, 7)) - 1]} ${r.month.slice(0, 4)}`} />
          </div>
          <p className="mt-3 text-xs text-gray-400">Kaynak: wunder.com.tr sipariş kayıtları (marka = {brandLabel}; iptal/iade düşülmemiş). Reklam tablolarındaki ciro platformun atfettiği değerdir; bu bölüm gerçekleşen satıştır.</p>
        </>
      )}
    </div>
  );
}

export default function BrandMonthlyReport({ config, children }) {
  const { title, campaignLike, ikasBrand, brandLabel, logo, presets, minDate, campaignNote } = config;
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || (typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null);
  const [startDate, setStartDate] = useState(() => presets[0].start());
  const [endDate, setEndDate] = useState(() => presets[0].end());
  const [costOverrideGoogle, setCostOverrideGoogle] = useState(null);
  const [costOverrideMeta, setCostOverrideMeta] = useState(null);
  const pdfRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try { await exportReportPdf(pdfRef.current, `${title.split(" · ")[0]}-${ymd(startDate)}_${ymd(endDate)}.pdf`); } finally { setPdfBusy(false); }
  };
  const [costInputGoogle, setCostInputGoogle] = useState('');
  const [costInputMeta, setCostInputMeta] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleData, setGoogleData] = useState([]);
  const [metaData, setMetaData] = useState([]);
  const [googleRows, setGoogleRows] = useState([]);
  const [metaRows, setMetaRows] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (brandId !== WUNDER_BRAND_ID) return;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const s = ymd(startDate);
        const e = ymd(endDate);
        const hdrs = { credentials: 'include', headers: { 'X-Brand-ID': brandId } };
        const [gRes, mRes] = await Promise.all([
          fetch(`${API}/api/google-ads/unified-campaign-performance?dateRange=custom&startDate=${s}&endDate=${e}`, hdrs),
          fetch(`${API}/api/google-ads/unified-meta-ga4-performance?dateRange=custom&startDate=${s}&endDate=${e}`, hdrs).catch(() => null),
        ]);
        if (!gRes.ok) throw new Error('Failed to fetch');
        const gRows = normalize(await gRes.json(), {
          impressions: 'google_impressions', clicks: 'google_clicks', cost: 'google_cost', conversions: 'google_conversions', revenue: 'google_conversion_value',
          ga4Sessions: 'ga4_sessions', ga4Purchases: 'ga4_purchases', ga4Revenue: 'ga4_total_revenue',
        }, campaignLike);
        const mRows = mRes && mRes.ok ? normalize(await mRes.json(), {
          impressions: 'impressions', clicks: 'clicks', cost: 'spend', conversions: 'conversions', revenue: 'conversion_value',
          ga4Sessions: 'ga4_sessions', ga4Purchases: 'ga4_purchases', ga4Revenue: 'ga4_total_revenue',
        }, campaignLike) : [];
        setGoogleRows(gRows);
        setMetaRows(mRows);
        setGoogleData(aggregate(gRows));
        setMetaData(aggregate(mRows));
      } catch (e) {
        console.error('BrandMonthlyReport fetch:', e);
        setError(e?.message || 'Failed to load');
        setGoogleData([]);
        setMetaData([]);
        setGoogleRows([]);
        setMetaRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [brandId, startDate, endDate, campaignLike]);

  const activePreset = useMemo(
    () => presets.find((p) => ymd(p.start()) === ymd(startDate) && ymd(p.end()) === ymd(endDate))?.label,
    [presets, startDate, endDate]
  );

  const applyCostOverride = () => {
    setCostOverrideGoogle(parseFloat(String(costInputGoogle).replace(/,/g, '.')) || null);
    setCostOverrideMeta(parseFloat(String(costInputMeta).replace(/,/g, '.')) || null);
  };

  const clearCostOverride = () => {
    setCostInputGoogle('');
    setCostInputMeta('');
    setCostOverrideGoogle(null);
    setCostOverrideMeta(null);
  };

  if (!brandId) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
          <p className="text-amber-800 font-medium">Lütfen bir marka seçin.</p>
        </div>
      </div>
    );
  }

  if (brandId !== WUNDER_BRAND_ID) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
          <p className="text-amber-800 font-medium">Bu sayfa yalnızca <strong>Wunder</strong> markası için kullanılabilir.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div ref={pdfRef}>
      <PdfHeader brandLogo={logo} brandName={brandLabel} title={title} subtitle="Google Ads & Meta Ads · wunder.com.tr satışları" startDate={startDate} endDate={endDate} />
      <div data-pdf-hide className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">{title}</h1>
          <p className="text-sm text-gray-500 mt-1">Google Ads & Meta Ads · wunder.com.tr satışları</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={downloadPdf}
            disabled={pdfBusy}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            PDF
          </button>
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 text-xs">
            {presets.map((p) => (
              <button
                key={p.label}
                onClick={() => { setStartDate(p.start()); setEndDate(p.end()); }}
                className={`px-3 py-1.5 rounded-md font-medium ${activePreset === p.label ? 'bg-gray-800 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">Start</span>
            <DatePicker
              selected={startDate}
              onChange={(d) => { if (d) { setStartDate(d); if (endDate && d > endDate) setEndDate(d); } }}
              minDate={minDate}
              maxDate={endDate}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm w-32"
              dateFormat="dd.MM.yyyy"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">End</span>
            <DatePicker
              selected={endDate}
              onChange={(d) => d && setEndDate(d)}
              minDate={startDate}
              className="px-3 py-2 border border-gray-200 rounded-xl text-sm w-32"
              dateFormat="dd.MM.yyyy"
            />
          </div>
        </div>
      </div>

      <Card data-pdf-card className="p-6 rounded-3xl bg-white border border-gray-200/80 shadow-sm">
        {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <>
            <PlatformSummary
              googleData={googleData}
              metaData={metaData}
              costOverrideGoogle={costOverrideGoogle}
              costOverrideMeta={costOverrideMeta}
            />
            <p className="text-xs text-gray-400 -mt-4">
              {campaignNote}, platform bazında toplam. Revenue = platformun atfettiği satış değeri; sepetteki tüm ürünleri kapsar.
            </p>
            <div className="mt-8">
              <MonthlySection
                googleRows={googleRows}
                metaRows={metaRows}
                googleData={googleData}
                metaData={metaData}
                costOverrideGoogle={costOverrideGoogle}
                costOverrideMeta={costOverrideMeta}
              />
            </div>
          </>
        )}

        <IkasSalesSection brandId={brandId} startDate={startDate} endDate={endDate} ikasBrand={ikasBrand} brandLabel={brandLabel} />
        {children && children(brandId, startDate, endDate)}
      </Card>
      <PdfFooter />
      </div>

      {/* Cost override panel - fixed bottom right, Glowy Drop düğmesinin solunda */}
      <div
        className="fixed bottom-4 flex flex-col gap-2 p-3 rounded-xl border border-gray-200 bg-white/95 shadow-lg backdrop-blur-sm"
        style={{ zIndex: 50, right: 208 }}
      >
        {(costOverrideGoogle != null || costOverrideMeta != null) && (
          <span className="text-[10px] text-amber-600 font-medium">Override active</span>
        )}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={costInputGoogle}
            onChange={(e) => setCostInputGoogle(e.target.value)}
            placeholder="Google cost"
            className="w-24 px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
          />
          <input
            type="text"
            value={costInputMeta}
            onChange={(e) => setCostInputMeta(e.target.value)}
            placeholder="Meta cost"
            className="w-24 px-2 py-1.5 text-sm border border-gray-200 rounded-lg"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={applyCostOverride} className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            Apply
          </button>
          <button onClick={clearCostOverride} className="px-3 py-1.5 text-xs font-medium bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}
